/**
 * FPT-8: chats and calls live in Meeting. Every other app — Mail first — can
 * only START one, by opening Meeting at one of these links:
 *
 *   <meeting>/start/chat?with=ann@x.com,bob@x.com&topic=Q3%20budget
 *   <meeting>/start/call?with=ann@x.com&media=audio
 *
 * `with` is the other people's email addresses (comma-separated; repeated
 * `with=` works too). `topic` names a new group chat (Mail passes the
 * thread's subject); `media` is "audio" (default) or "video" for a call.
 * One person opens their 1:1 chat; several open the group chat with exactly
 * them. A call rings the people of that chat.
 *
 * The builder (used by Mail) and the parser (used by Meeting's /start page)
 * live here together so the two ends can't drift.
 */

export type MeetingStartAction = "chat" | "call";
export type MeetingCallMedia = "audio" | "video";

export interface MeetingStartLink {
    action: MeetingStartAction;
    /** Lower-cased, de-duplicated email addresses of the other people. */
    with: string[];
    topic: string | null;
    media: MeetingCallMedia;
}

/** Most people one link names; the server takes up to 100 too. */
export const MEETING_LINK_MAX_PEOPLE = 100;

const EMAIL = /^[^\s@<>,;]+@[^\s@<>,;]+$/;

/** Trimmed, lower-cased, de-duplicated addresses; `exclude` (your own) dropped. */
export function cleanEmails(raw: Iterable<string>, exclude?: string | null): string[] {
    const mine = (exclude ?? "").trim().toLowerCase();
    const out: string[] = [];
    for (const entry of raw) {
        for (const part of entry.split(/[,;]/)) {
            const e = part.trim().replace(/^<|>$/g, "").toLowerCase();
            if (!EMAIL.test(e) || e === mine || out.includes(e)) continue;
            out.push(e);
        }
    }
    return out.slice(0, MEETING_LINK_MAX_PEOPLE);
}

/** The Meeting URL that opens (or starts) a chat or call with these people. */
export function buildMeetingStartUrl(
    meetingBaseUrl: string,
    link: { action: MeetingStartAction; with: string[]; topic?: string | null; media?: MeetingCallMedia }
): string {
    const url = new URL(`start/${link.action}`, meetingBaseUrl.endsWith("/") ? meetingBaseUrl : `${meetingBaseUrl}/`);
    url.searchParams.set("with", cleanEmails(link.with).join(","));
    const topic = link.topic?.trim();
    if (topic) url.searchParams.set("topic", topic.slice(0, 200));
    if (link.action === "call") url.searchParams.set("media", link.media === "video" ? "video" : "audio");
    return url.toString();
}

/** Reads a /start link; null when the action is unknown or nobody is named. */
export function parseMeetingStartLink(action: string | undefined, search: string | URLSearchParams): MeetingStartLink | null {
    if (action !== "chat" && action !== "call") return null;
    const params = typeof search === "string" ? new URLSearchParams(search) : search;
    const people = cleanEmails(params.getAll("with"));
    if (people.length === 0) return null;
    const topic = params.get("topic")?.trim() || null;
    return {
        action,
        with: people,
        topic: topic ? topic.slice(0, 200) : null,
        media: params.get("media") === "video" ? "video" : "audio"
    };
}
